import { useMemo } from 'react';
import { LuLayers, LuPentagon } from 'react-icons/lu';

import DetectionSummary from '@components/detection/DetectionSummary';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { formatAoiArea, getAoiStats } from '@utils/aoiStats';
import { AREA_MODE } from '@utils/constants';
import { DETECTION_OUTCOME, getScanAreaSquareMeters } from '@utils/detectionOutcome';

/**
 * Step 3 of the detection wizard: the single confirmation before a run starts.
 *
 * The step is only reachable once the backend has confirmed a run is actually
 * needed (see `useRunDetection` — the Area step's Continue previews first), so
 * this is purely "here's what will run, say the word". An area that's already
 * been analyzed never gets here.
 *
 * Reads the run's configuration from the picker store and hands it to the shared
 * {@link DetectionSummary}, which the map's boundary-edit confirmation dialog
 * renders too.
 */
const RunStep = () => {
	const objects = useObjectPickerStore(s => s.selectedObjects);
	const sourceCollectionName = useObjectPickerStore(s => s.sourceCollectionName);
	const areaMode = useObjectPickerStore(s => s.areaMode);
	const aoiPolygon = useObjectPickerStore(s => s.aoiPolygon);
	const preview = useObjectPickerStore(s => s.detectionPreview);

	const isDraw = areaMode === AREA_MODE.DRAW;
	const aoiStats = useMemo(() => (isDraw ? getAoiStats(aoiPolygon) : null), [isDraw, aoiPolygon]);
	const aoiAreaLabel = aoiStats ? formatAoiArea(aoiStats.squareMeters) : null;

	const areaLabel = isDraw
		? `Drawn area of interest${aoiAreaLabel ? ` · ${aoiAreaLabel}` : ''}`
		: 'Entire project';

	return (
		<DetectionSummary
			heading="Confirm this detection run"
			description="Nothing has started yet. Check what will be analyzed, then confirm to start the run."
			collectionName={sourceCollectionName}
			objects={objects}
			areaLabel={areaLabel}
			areaIcon={isDraw ? <LuPentagon /> : <LuLayers />}
			scanAreaSquareMeters={getScanAreaSquareMeters(preview)}
			outcome={DETECTION_OUTCOME.NEEDS_CONFIRMATION}
		/>
	);
};

export default RunStep;
